
// FORCE UPDATE v13
import React, { useEffect, lazy, Suspense } from 'react';
import { HashRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { logger } from './utils/logger';

// Critical Paths - NO LAZY (precisa ser rápido)
import { LandingPage } from './pages/LandingPage';
import { ScrollToTop } from './components/ScrollToTop';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
// PWAInstallPrompt é importado e usado em Auth.tsx (só na tela de login)
import { CookieConsent } from './components/CookieConsent';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PWALandingRouter } from './components/PWALandingRouter';
import { PWAUpdateBanner } from './components/PWAUpdateBanner';
import { OfflineIndicator } from './components/OfflineIndicator';
import { ErrorBoundary } from './components/ErrorBoundary';

// Helper para recarregar a página se falhar o carregamento do chunk (versão nova deployada)
const lazyWithRetry = (componentImport: () => Promise<any>) =>
  lazy(async () => {
    try {
      return await componentImport();
    } catch (error: any) {
      logger.error("Erro ao carregar módulo dinâmico:", error);
      // Verifica se é erro de chunk (network error ou failed to fetch)
      if (error.message?.includes('Failed to fetch dynamically imported module') || error.message?.includes('Importing a module script failed')) {
        // Evita loop infinito de reloads se o servidor estiver realmente fora
        const storageKey = `retry-lazy-${window.location.pathname}`;
        const lastRetry = sessionStorage.getItem(storageKey);
        const now = Date.now();

        if (!lastRetry || (now - parseInt(lastRetry) > 10000)) {
          sessionStorage.setItem(storageKey, now.toString());
          window.location.reload();
        }
      }
      throw error;
    }
  });

const FirebaseActionHandler = () => {
  // HashRouter ignora a querystring real da URL (que fica antes do #).
  // O Firebase envia ?mode=resetPassword ANTES do hash (ex: /?mode=resetPassword#/)
  // Então precisamos ler o window.location.search nativo.
  const realSearchParams = new URLSearchParams(window.location.search);
  const mode = realSearchParams.get('mode');
  
  if (mode === 'resetPassword') {
    return <Navigate to={`/reset-password?${realSearchParams.toString()}`} replace />;
  }
  return <LandingPage />;
};

// Lazy Loading - Dashboards (pesado)
const Auth = lazyWithRetry(() => import('./pages/Auth').then(m => ({ default: m.Auth })));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const PsychDashboard = lazyWithRetry(() => import('./pages/psychologist/PsychDashboard').then(m => ({ default: m.PsychDashboard })));
const PatientDashboard = lazyWithRetry(() => import('./pages/patient/PatientDashboard').then(m => ({ default: m.PatientDashboard })));
const AuroraDashboard = lazyWithRetry(() => import('./pages/admin/AuroraDashboard').then(m => ({ default: m.AuroraDashboard })));

// Lazy Loading - Psychologist Pages
const PatientsList = lazyWithRetry(() => import('./pages/psychologist/PatientsList').then(m => ({ default: m.PatientsList })));
const FormConfigPage = lazyWithRetry(() => import('./pages/psychologist/FormConfigPage').then(m => ({ default: m.FormConfigPage })));
const Schedule = lazyWithRetry(() => import('./pages/psychologist/Schedule').then(m => ({ default: m.Schedule })));
const Account = lazyWithRetry(() => import('./pages/psychologist/Account').then(m => ({ default: m.Account })));
const ReportViewer = lazyWithRetry(() => import('./pages/psychologist/ReportViewer').then(m => ({ default: m.ReportViewer })));
const Plans = lazyWithRetry(() => import('./pages/psychologist/Plans').then(m => ({ default: m.Plans })));
const Financial = lazyWithRetry(() => import('./pages/psychologist/Financial').then(m => ({ default: m.Financial })));
const TeamManagement = lazyWithRetry(() => import('./pages/psychologist/TeamManagement').then(m => ({ default: m.TeamManagement })));
const ReadOnlyDashboard = lazyWithRetry(() => import('./pages/psychologist/ReadOnlyDashboard').then(m => ({ default: m.ReadOnlyDashboard })));

// Lazy Loading - Settings Pages
const DataExport = lazyWithRetry(() => import('./pages/settings/DataExport').then(m => ({ default: m.DataExport })));
const DeleteAccount = lazyWithRetry(() => import('./pages/settings/DeleteAccount').then(m => ({ default: m.DeleteAccount })));

// Lazy Loading - Patient Pages
const DailyDiary = lazyWithRetry(() => import('./pages/patient/DailyDiary').then(m => ({ default: m.DailyDiary })));
const PatientSchedule = lazyWithRetry(() => import('./pages/patient/PatientSchedule').then(m => ({ default: m.PatientSchedule })));
const PatientAccount = lazyWithRetry(() => import('./pages/patient/PatientAccount').then(m => ({ default: m.PatientAccount })));
const ChangePassword = lazyWithRetry(() => import('./pages/patient/ChangePassword').then(m => ({ default: m.ChangePassword })));

// Lazy Loading - Admin Pages
const AuroraLogin = lazyWithRetry(() => import('./pages/admin/AuroraLogin').then(m => ({ default: m.AuroraLogin })));
const AuroraInbox = lazyWithRetry(() => import('./pages/admin/AuroraInbox').then(m => ({ default: m.AuroraInbox })));
const AuroraUsers = lazyWithRetry(() => import('./pages/admin/AuroraUsers').then(m => ({ default: m.AuroraUsers })));
const AuroraTeam = lazyWithRetry(() => import('./pages/admin/AuroraTeam').then(m => ({ default: m.AuroraTeam })));
const AuroraLogs = lazyWithRetry(() => import('./pages/admin/AuroraLogs').then(m => ({ default: m.AuroraLogs })));
const DebugPage = lazyWithRetry(() => import('./pages/admin/DebugPage').then(m => ({ default: m.DebugPage })));

// Lazy Loading - Legal Pages
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazyWithRetry(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));

// Loading Fallback Component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-bg dark:bg-darkBg">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
    </div>
  </div>
);

// Duplicate theme logic removed as it's now handled by ThemeProvider

const App: React.FC = () => {
  return (
    <ToastProvider>
      <AuthProvider>
        <ThemeProvider>
          <TimezoneProvider>
            {/* PWAInstallPrompt renderizado apenas em Auth.tsx */}
            <PWAUpdateBanner />
            <OfflineIndicator />
            <HashRouter>
              <ScrollToTop />
              <main className="font-sans text-text antialiased">
                <ErrorBoundary>
                  <Routes>
                    {/* Interceptador de Action URL do Firebase (caso a URL não tenha /reset-password configurado no console) */}
                    <Route path="/" element={
                      <>
                        <PWALandingRouter />
                        <FirebaseActionHandler />
                      </>
                    } />
                    
                    {/* Rota principal de Reset Password */}
                    <Route path="/reset-password" element={
                      <Suspense fallback={<LoadingFallback />}>
                        <ResetPassword />
                      </Suspense>
                    } />

                    <Route path="/login" element={
                      <Suspense fallback={<LoadingFallback />}>
                        <Auth />
                      </Suspense>
                    } />
                    <Route path="/plans" element={
                      <Suspense fallback={<LoadingFallback />}>
                        <Plans />
                      </Suspense>
                    } />
                    <Route path="/privacy" element={
                      <Suspense fallback={<LoadingFallback />}>
                        <PrivacyPage />
                      </Suspense>
                    } />
                    <Route path="/terms" element={
                      <Suspense fallback={<LoadingFallback />}>
                        <TermsPage />
                      </Suspense>
                    } />

                    {/* Rotas de Psicólogo - Dashboards com Lazy Loading */}
                    <Route element={<ProtectedRoute />}>
                      <Route path="/psych/dashboard" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <PsychDashboard />
                        </Suspense>
                      } />
                      <Route path="/psych/patients" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <PatientsList />
                        </Suspense>
                      } />
                      <Route path="/psych/patients/:id/report" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <ReportViewer />
                        </Suspense>
                      } />
                      <Route path="/psych/forms" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <FormConfigPage />
                        </Suspense>
                      } />
                      <Route path="/psych/schedule" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <Schedule />
                        </Suspense>
                      } />
                      <Route path="/psych/account" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <Account />
                        </Suspense>
                      } />
                      <Route path="/psych/team" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <TeamManagement />
                        </Suspense>
                      } />
                      <Route path="/financial" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <Financial />
                        </Suspense>
                      } />
                      <Route path="/psych/export-data" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <DataExport />
                        </Suspense>
                      } />
                      <Route path="/psych/delete-account" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <DeleteAccount />
                        </Suspense>
                      } />
                      <Route path="/psych/change-password" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <ChangePassword />
                        </Suspense>
                      } />
                      <Route path="/psych/read-only" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <ReadOnlyDashboard />
                        </Suspense>
                      } />
                    </Route>

                    {/* Rotas de Paciente - Dashboard com Lazy Loading */}
                    <Route element={<ProtectedRoute />}>
                      <Route path="/patient/dashboard" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <PatientDashboard />
                        </Suspense>
                      } />
                      <Route path="/patient/diary" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <DailyDiary />
                        </Suspense>
                      } />
                      <Route path="/patient/schedule" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <PatientSchedule />
                        </Suspense>
                      } />
                      <Route path="/patient/account" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <PatientAccount />
                        </Suspense>
                      } />
                      <Route path="/patient/change-password" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <ChangePassword />
                        </Suspense>
                      } />
                    </Route>

                    {/* Rotas Admin Aurora - Dashboard com Lazy Loading */}
                    <Route path="/aurora" element={<Navigate to="/aurora/login" replace />} />
                    <Route path="/aurora/login" element={
                      <Suspense fallback={<LoadingFallback />}>
                        <AuroraLogin />
                      </Suspense>
                    } />
                    <Route element={<ProtectedRoute redirectPath="/aurora/login" />}>
                      <Route path="/aurora/debug" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <DebugPage />
                        </Suspense>
                      } />
                      <Route path="/aurora/dashboard" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <AuroraDashboard />
                        </Suspense>
                      } />
                      <Route path="/aurora/inbox" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <AuroraInbox />
                        </Suspense>
                      } />
                      <Route path="/aurora/crm" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <AuroraUsers />
                        </Suspense>
                      } />
                      <Route path="/aurora/team" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <AuroraTeam />
                        </Suspense>
                      } />
                      <Route path="/aurora/logs" element={
                        <Suspense fallback={<LoadingFallback />}>
                          <AuroraLogs />
                        </Suspense>
                      } />
                    </Route>

                    {/* Redirect Padrão */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </ErrorBoundary>
              </main>
            </HashRouter>
          </TimezoneProvider>
        </ThemeProvider>
      </AuthProvider>
    </ToastProvider>
  );
};

export default App;
